import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { supabase } from "../lib/supabase";
import {
  ApiError,
  createUser,
  getMe,
  loginAccessToken,
  updateMe,
  type UserPublic,
} from "../lib/api";

interface User {
  id?: string;
  email: string;
  name?: string;
  school?: string;
  phone?: string;
  city?: string;
  avatarUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<boolean>;
  register: (
    name: string,
    email: string,
    school: string,
    password: string,
  ) => Promise<boolean>;
  updateProfile: (data: {
    name?: string;
    school?: string;
    phone?: string;
    city?: string;
    avatarUrl?: string;
  }) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const STORAGE_KEYS = {
  isAuthenticated: "isAuthenticated",
  userData: "userData",
  accessToken: "accessToken",
  expiresAt: "authExpiresAt",
} as const;

function getStorageWithToken(): Storage | null {
  const localToken = localStorage.getItem(STORAGE_KEYS.accessToken);
  if (localToken && localToken.trim()) return localStorage;
  const sessionToken = sessionStorage.getItem(STORAGE_KEYS.accessToken);
  if (sessionToken && sessionToken.trim()) return sessionStorage;
  return null;
}

function readStoredUser(): User | null {
  const raw =
    localStorage.getItem(STORAGE_KEYS.userData) ||
    sessionStorage.getItem(STORAGE_KEYS.userData);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

function writeStoredUser(user: User | null, persist: boolean = true) {
  const storage = persist ? localStorage : sessionStorage;
  if (!user) {
    localStorage.removeItem(STORAGE_KEYS.userData);
    sessionStorage.removeItem(STORAGE_KEYS.userData);
    return;
  }
  storage.setItem(STORAGE_KEYS.userData, JSON.stringify(user));
}

function readStoredToken(): string | null {
  const token =
    localStorage.getItem(STORAGE_KEYS.accessToken) ||
    sessionStorage.getItem(STORAGE_KEYS.accessToken);
  return token && token.trim() ? token : null;
}

function writeStoredToken(token: string | null, persist: boolean = true) {
  const storage = persist ? localStorage : sessionStorage;
  if (!token) {
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    sessionStorage.removeItem(STORAGE_KEYS.accessToken);
    return;
  }
  storage.setItem(STORAGE_KEYS.accessToken, token);
}

function readStoredExpiresAt(): number | null {
  const raw =
    localStorage.getItem(STORAGE_KEYS.expiresAt) ||
    sessionStorage.getItem(STORAGE_KEYS.expiresAt);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function writeStoredExpiresAt(
  expiresAt: number | null,
  persist: boolean = true,
) {
  const storage = persist ? localStorage : sessionStorage;
  if (!expiresAt) {
    localStorage.removeItem(STORAGE_KEYS.expiresAt);
    sessionStorage.removeItem(STORAGE_KEYS.expiresAt);
    return;
  }
  storage.setItem(STORAGE_KEYS.expiresAt, String(expiresAt));
}

function isExpired(expiresAtMs: number): boolean {
  return Date.now() >= expiresAtMs;
}

function daysFromNowMs(days: number): number {
  return Date.now() + days * 24 * 60 * 60 * 1000;
}

function mapUserPublic(user: UserPublic): User {
  return {
    id: user.id,
    email: user.email,
    name: user.full_name ?? undefined,
    school: user.institution ?? undefined,
    phone: user.phone ?? undefined,
    city: user.city ?? undefined,
    avatarUrl: user.avatar_url ?? undefined,
    createdAt: user.created_at ?? undefined,
    updatedAt: user.updated_at ?? undefined,
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const handleAuthError = (event: any) => {
      logout();
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login?expired=true";
      }
    };

    window.addEventListener("auth-error", handleAuthError);
    return () => window.removeEventListener("auth-error", handleAuthError);
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Detect recovery token in the URL hash (Supabase Auth)
    const hash = window.location.hash;
    const search = window.location.search;

    if (
      hash &&
      (hash.includes("type=recovery") || hash.includes("access_token="))
    ) {
      const params = new URLSearchParams(hash.replace("#", "?"));
      const accessToken = params.get("access_token");
      const type = params.get("type");

      if (
        accessToken &&
        (type === "recovery" || hash.includes("type=recovery"))
      ) {
        // Force Supabase to immediately recognize the session from the token in the hash (recovery flow)
        supabase.auth
          .setSession({
            access_token: accessToken,
            refresh_token: params.get("refresh_token") || "",
          })
          .then(({ error }: { error: any }) => {
            if (error)
              console.error("Erro ao definir sessão no Supabase:", error);
          });

        writeStoredToken(accessToken, false);
        writeStoredExpiresAt(daysFromNowMs(1), false);
        setToken(accessToken);
        setIsAuthenticated(true);
        // We do not clear the hash immediately here so other components can also detect it if necessary
        return;
      }
    }

    const token = readStoredToken();
    setToken(token);
    if (!token) return;

    const expiresAt = readStoredExpiresAt();
    if (expiresAt && isExpired(expiresAt)) {
      writeStoredToken(null);
      writeStoredExpiresAt(null);
      writeStoredUser(null);
      localStorage.removeItem(STORAGE_KEYS.isAuthenticated);
      sessionStorage.removeItem(STORAGE_KEYS.isAuthenticated);
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      return;
    }

    if (!expiresAt) {
      const storage = getStorageWithToken();
      if (storage) {
        const isPersistent = storage === localStorage;
        writeStoredExpiresAt(daysFromNowMs(1), isPersistent);
      }
    }

    const storedUser = readStoredUser();
    setIsAuthenticated(true);
    if (storedUser) setUser(storedUser);

    (async () => {
      try {
        if (token === "debug-token") return;
        const me = await getMe(token);
        if (cancelled) return;
        const mapped = mapUserPublic(me);
        const isPersistent = getStorageWithToken() === localStorage;
        writeStoredUser(mapped, isPersistent);

        if (isPersistent) {
          localStorage.setItem(STORAGE_KEYS.isAuthenticated, "true");
        } else {
          sessionStorage.setItem(STORAGE_KEYS.isAuthenticated, "true");
        }

        setUser(mapped);
      } catch (err) {
        if (cancelled) return;

        // During the password recovery flow, the API may fail to fetch the profile.
        // In this situation we should NOT force logout, as the user is still in a valid Supabase recovery flow.
        const hasRecovery =
          window.location.hash.includes("type=recovery") ||
          window.location.hash.includes("access_token=") ||
          window.location.pathname.includes("/reset-password");

        if (!hasRecovery) {
          logout();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const logout = () => {
    writeStoredToken(null);
    writeStoredExpiresAt(null);
    writeStoredUser(null);
    localStorage.removeItem(STORAGE_KEYS.isAuthenticated);
    sessionStorage.removeItem(STORAGE_KEYS.isAuthenticated);
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
  };

  const login = async (
    email: string,
    password: string,
    rememberMe: boolean = false,
  ): Promise<boolean> => {
    try {
      const token = await loginAccessToken(email, password);
      
      const storage = localStorage;

      try {
        writeStoredToken(token.access_token, true);
        writeStoredExpiresAt(daysFromNowMs(rememberMe ? 3 : 1), true);
        storage.setItem(STORAGE_KEYS.isAuthenticated, "true");
      } catch (e: any) {
        console.error(`Erro ao salvar no localStorage: ${e.message}`);
        console.error(e);
      }

      const me = await getMe(token.access_token);
      
      const mapped = mapUserPublic(me);
      writeStoredUser(mapped, true);

      setIsAuthenticated(true);
      setUser(mapped);
      setToken(token.access_token);
      return true;
    } catch (e: any) {
      console.error(`Erro fatal no login: ${e.message}`);
      console.error(e);
      throw e;
    }
  };

  const register = async (
    name: string,
    email: string,
    school: string,
    password: string,
  ): Promise<boolean> => {
    await createUser({ name, email, school, password });
    try {
      return await login(email, password);
    } catch (error) {
      if (error instanceof ApiError) {
        return false;
      }
      throw error;
    }
  };

  const updateProfile = async (data: {
    name?: string;
    school?: string;
    phone?: string;
    city?: string;
    avatarUrl?: string;
  }): Promise<boolean> => {
    if (!token) return false;
    const isPersistent = !!localStorage.getItem(STORAGE_KEYS.accessToken);
    const updated = await updateMe(
      {
        full_name: data.name,
        institution: data.school,
        phone: data.phone,
        city: data.city,
        avatar_url: data.avatarUrl,
      },
      token,
    );
    const mapped = mapUserPublic(updated);
    writeStoredUser(mapped, isPersistent);
    setUser(mapped);
    return true;
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token,
        login,
        register,
        updateProfile,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }

  return context;
}
